import React, { useState } from 'react';
import {
    Box,
    Button,
    Link,
    Modal,
    Spinner,
    SpaceBetween,
    StatusIndicator,
} from '@cloudscape-design/components';
import { seekingAlphaApi } from '../../client/clients';
import { ArticleDetailsResponse, GetArticleDetailsCredentialKeyEnum } from '../../api';
import { useFlashbar } from '../../context/FlashbarContext';

// ─── ArticleContent ───────────────────────────────────────────────────────────
// Renders HTML safely; fixes broken images; constrains image dimensions.

export const ArticleContent: React.FC<{ html: string; fallbackAlt: string }> = ({ html, fallbackAlt }) => {
    const ref = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const root = ref.current;
        if (!root) return;
        const imgs = root.querySelectorAll('img');
        imgs.forEach((img) => {
            if (!img.getAttribute('alt')) img.setAttribute('alt', fallbackAlt);
            img.addEventListener('error', () => {
                const placeholder = document.createElement('div');
                placeholder.textContent = 'Image failed to load';
                placeholder.style.cssText = [
                    'padding:12px',
                    'background:#f4f4f4',
                    'border:1px dashed #aab7b8',
                    'border-radius:4px',
                    'color:#5f6b7a',
                    'font-size:12px',
                    'text-align:center',
                    'margin:10px 0',
                ].join(';');
                img.replaceWith(placeholder);
            }, { once: true });
        });
    }, [html, fallbackAlt]);

    return (
        <div
            ref={ref}
            className="article-content"
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

// ─── ArticleDetailsModal ──────────────────────────────────────────────────────
// Full article view — mirrors the SeekingAlpha Articles page modal exactly.

export interface ArticleDetailsModalProps {
    data: ArticleDetailsResponse;
    onClose: () => void;
}

export const ArticleDetailsModal: React.FC<ArticleDetailsModalProps> = ({ data, onClose }) => {
    const article = data.data;
    const included = (data.included ?? []) as any[];
    const authors = included.filter((item: any) => item.type === 'author');
    const tags    = included.filter((item: any) => item.type === 'tag');

    const title = article?.attributes?.title ?? 'Article details';

    const formatDate = (d?: string) => {
        if (!d) return 'Unknown date';
        try { return new Date(d).toLocaleString(); }
        catch { return d; }
    };

    return (
        <Modal
            visible={true}
            onDismiss={onClose}
            size="large"
            header={title}
            footer={
                <Box float="right">
                    <Button variant="primary" onClick={onClose}>Close</Button>
                </Box>
            }
        >
            {!article ? (
                <Box>No article data available.</Box>
            ) : (
                <SpaceBetween size="m">
                    {(data as any).tickerValidationMethod && (
                        <Box>
                            <StatusIndicator
                                type={(data as any).tickerValidationMethod === 'IN_HOUSE_VALIDATED' ? 'info' : 'success'}
                            >
                                {(data as any).tickerValidationMethod === 'IN_HOUSE_VALIDATED'
                                    ? 'In-house validated'
                                    : 'AI verified'}
                            </StatusIndicator>
                        </Box>
                    )}

                    {data.buyTickers && data.buyTickers.length > 0 && (
                        <Box>
                            <Box variant="awsui-key-label">Buy recommendations ({data.buyTickers.length})</Box>
                            <Box margin={{ top: 'xs' }}>
                                <SpaceBetween direction="horizontal" size="xs">
                                    {data.buyTickers.map((ticker: string) => (
                                        <span
                                            key={`buy-${ticker}`}
                                            style={{
                                                backgroundColor: '#1d8102',
                                                color: 'white',
                                                padding: '4px 10px',
                                                borderRadius: 4,
                                                fontSize: 13,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {ticker}
                                        </span>
                                    ))}
                                </SpaceBetween>
                            </Box>
                        </Box>
                    )}

                    {(data as any).sellTickers && (data as any).sellTickers.length > 0 && (
                        <Box>
                            <Box variant="awsui-key-label">Sell recommendations ({(data as any).sellTickers.length})</Box>
                            <Box margin={{ top: 'xs' }}>
                                <SpaceBetween direction="horizontal" size="xs">
                                    {(data as any).sellTickers.map((ticker: string) => (
                                        <span
                                            key={`sell-${ticker}`}
                                            style={{
                                                backgroundColor: '#b30000',
                                                color: 'white',
                                                padding: '4px 10px',
                                                borderRadius: 4,
                                                fontSize: 13,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {ticker}
                                        </span>
                                    ))}
                                </SpaceBetween>
                            </Box>
                        </Box>
                    )}

                    <Box>
                        <SpaceBetween direction="horizontal" size="m">
                            <Box>
                                <Box variant="awsui-key-label">Published</Box>
                                {formatDate(article.attributes?.publishOn)}
                            </Box>
                            <Box>
                                <Box variant="awsui-key-label">Comments</Box>
                                {article.attributes?.commentCount ?? 0}
                            </Box>
                        </SpaceBetween>
                    </Box>

                    {article.attributes?.summary && article.attributes.summary.length > 0 && (
                        <Box>
                            <Box variant="awsui-key-label">Summary</Box>
                            <ul style={{ paddingLeft: 20, lineHeight: 1.5, marginTop: 4 }}>
                                {article.attributes.summary.map((point: string, idx: number) => (
                                    <li key={idx}>{point}</li>
                                ))}
                            </ul>
                        </Box>
                    )}

                    {(() => {
                        const EXCLUDED_TYPES = new Set(['author', 'tag', 'service_plan', 'service_plan_article', 'user']);
                        const mainHtml = article.attributes?.content ?? '';
                        // Fallback: for presentation-style Pro articles the body lives in
                        // included items (presentation_slide etc.) rather than attributes.content.
                        const slideHtmls: string[] = mainHtml ? [] : (included as any[])
                            .filter(item => !EXCLUDED_TYPES.has(item.type) && typeof item.attributes?.content === 'string')
                            .map(item => item.attributes.content as string);

                        if (!mainHtml && slideHtmls.length === 0) return null;

                        const contentStyle = {
                            padding: 15,
                            backgroundColor: '#fafbfc',
                            borderRadius: 8,
                            border: '1px solid #d5dbdb',
                            lineHeight: 1.5,
                            fontSize: 14,
                            maxHeight: 400,
                            overflow: 'auto',
                        };

                        return (
                            <Box>
                                <Box variant="awsui-key-label">Full content</Box>
                                <style dangerouslySetInnerHTML={{ __html: `
                                    .article-content img {
                                        max-width: 100% !important;
                                        max-height: 400px !important;
                                        height: auto !important;
                                        display: block;
                                        margin: 10px 0;
                                        border-radius: 4px;
                                        object-fit: contain;
                                    }
                                    .article-content figure {
                                        max-width: 100% !important;
                                        margin: 10px 0 !important;
                                    }
                                `}} />
                                <div style={contentStyle}>
                                    {mainHtml
                                        ? <ArticleContent html={mainHtml} fallbackAlt={title} />
                                        : slideHtmls.map((html, i) => (
                                            <ArticleContent key={i} html={html} fallbackAlt={title} />
                                        ))
                                    }
                                </div>
                            </Box>
                        );
                    })()}

                    {authors.length > 0 && (
                        <Box>
                            <Box variant="awsui-key-label">Authors</Box>
                            <SpaceBetween size="xs">
                                {authors.map((author: any) => (
                                    <Box key={author.id} padding="xs">
                                        <strong>{author.attributes?.nick ?? 'Unknown'}</strong>
                                        {author.attributes?.company && (
                                            <span style={{ color: '#5f6b7a', marginLeft: 8 }}>
                                                — {author.attributes.company}
                                            </span>
                                        )}
                                    </Box>
                                ))}
                            </SpaceBetween>
                        </Box>
                    )}

                    {tags.length > 0 && (
                        <Box>
                            <Box variant="awsui-key-label">Related tickers</Box>
                            <SpaceBetween direction="horizontal" size="xs">
                                {tags.map((tag: any) => (
                                    <span
                                        key={tag.id}
                                        style={{
                                            backgroundColor: '#e9ebed',
                                            padding: '4px 10px',
                                            borderRadius: 16,
                                            fontSize: 12,
                                        }}
                                    >
                                        {tag.attributes?.name}
                                    </span>
                                ))}
                            </SpaceBetween>
                        </Box>
                    )}

                    {article.links?.self && (
                        <Box>
                            <Link
                                href={`https://seekingalpha.com${article.links.self}`}
                                external
                                externalIconAriaLabel="Opens in a new tab"
                            >
                                View original article on SeekingAlpha
                            </Link>
                        </Box>
                    )}
                </SpaceBetween>
            )}
        </Modal>
    );
};

// ─── useArticlePopup hook ──────────────────────────────────────────────────────

export interface UseArticlePopupResult {
    openArticle: (articleId: string, credentialKey?: GetArticleDetailsCredentialKeyEnum) => void;
    loadingArticleId: string | null;
    modalElement: React.ReactElement | null;
}

export function useArticlePopup(): UseArticlePopupResult {
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [articleData, setArticleData] = useState<ArticleDetailsResponse | null>(null);
    const [open, setOpen] = useState(false);
    const flashbar = useFlashbar();

    const openArticle = async (
        articleId: string,
        credentialKey: GetArticleDetailsCredentialKeyEnum = GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie,
    ) => {
        if (!articleId) return;
        setLoadingId(articleId);
        try {
            const response = await seekingAlphaApi.getArticleDetails(credentialKey, articleId);
            setArticleData(response.data);
            setOpen(true);
        } catch (err: any) {
            console.error('Failed to load article content:', err);
            flashbar.push({
                type: 'error',
                header: 'Failed to load article',
                content: err?.response?.data?.message ?? err?.message ?? 'Unknown error',
            });
        } finally {
            setLoadingId(null);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setArticleData(null);
    };

    const modalElement = open && articleData
        ? <ArticleDetailsModal data={articleData} onClose={handleClose} />
        : null;

    return { openArticle, loadingArticleId: loadingId, modalElement };
}

// ─── OpenArticleButton ────────────────────────────────────────────────────────

interface OpenArticleButtonProps {
    articleId?: string;
    onOpen: (articleId: string) => void;
    loadingId: string | null;
}

export const OpenArticleButton: React.FC<OpenArticleButtonProps> = ({ articleId, onOpen, loadingId }) => {
    if (!articleId) return null;
    const isLoading = loadingId === articleId;
    return (
        <Button
            variant="link"
            loading={isLoading}
            disabled={isLoading}
            onClick={() => onOpen(articleId)}
        >
            {isLoading ? 'Loading…' : 'Open Article'}
        </Button>
    );
};

// ─── InlineSpinner ────────────────────────────────────────────────────────────

export const InlineSpinner: React.FC<{ show: boolean }> = ({ show }) =>
    show ? <Spinner size="normal" /> : null;
